/**
 * Bootstrap do script injetável.
 *
 *   <script src="https://SEU-HOST/widget.js" data-webchat-id="pk_..." async></script>
 *
 * Ordem das checagens, e por que ela é assim:
 *
 *  1. pede a config ao backend. Se vier `403`, **nada é renderizado** — a barreira real do domain
 *     binding é o servidor não entregar a config; sem config não há fluxo, pergunta nem código;
 *  2. com a config em mão, compara `location.hostname` com a `authorizedDomains` antes de desenhar.
 *     Isso é *fail-fast* de UX (não deixa um widget aparecer para depois morrer) e defesa em
 *     profundidade — não é garantia, porque checagem client-side é sempre burlável.
 *
 * Todo caminho de falha grita no console com o que fazer. Widget que falha calado vira ticket.
 */

import { matchesDomain } from '@merlin/flow-engine';
import { fetchConfig } from './transport.js';
import { mountWidget, type WidgetEvent, type WidgetInstance } from './ui/widget.js';
import { SDK_VERSION } from './version.js';

const LOG_PREFIX = '[merlin-webchat]';

export interface MerlinWebchatApi {
  open(): void;
  close(): void;
  toggle(): void;
  reset(): void;
  getVariables(): Record<string, unknown>;
  on(handler: (event: WidgetEvent) => void): () => void;
  readonly ready: Promise<WidgetInstance | null>;
  /** Versão do bundle carregado. O suporte pergunta isso; melhor ter resposta. */
  readonly version: string;
}

function findScriptTag(): HTMLScriptElement | null {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement && current.dataset.webchatId) return current;
  return document.querySelector<HTMLScriptElement>('script[data-webchat-id]');
}

/**
 * Origem que serve os assets do widget: a mesma que serviu o script.
 *
 * Não sai de `apiBase`, que pode ter sido redirecionado por `data-api-base` para outro host. Vale
 * tanto para `/widget.js` quanto para `/widget/<versão>/widget.js`, porque só a origem é usada.
 */
function resolveAssetBase(script: HTMLScriptElement | null): string {
  try {
    return script?.src ? new URL(script.src).origin : '';
  } catch {
    return '';
  }
}

/**
 * Sem `data-api-base`, assume que as rotas estão em `/api` no mesmo host que serviu o script.
 *
 * **No nosso deploy esse palpite está sempre errado**, e desde que a API foi para a VM ele erra de um
 * jeito silencioso: `/api/*` no Hosting cai no rewrite `**` do SPA e devolve `index.html` com 200. O
 * palpite fica porque serve auto-hospedagem (widget e API no mesmo host), e o painel sempre emite o
 * atributo. Quem diagnostica é o `fetchConfig`, que reconhece HTML e diz o que conferir.
 */
function resolveApiBase(
  script: HTMLScriptElement | null,
): { base: string; adivinhado: boolean } | null {
  const explicit = script?.dataset.apiBase?.trim();
  if (explicit) return { base: explicit.replace(/\/$/, ''), adivinhado: false };
  const src = script?.src;
  if (!src) return null;
  try {
    return { base: `${new URL(src).origin}/api`, adivinhado: true };
  } catch {
    return null;
  }
}

function whenDomReady(): Promise<void> {
  if (document.readyState === 'loading') {
    return new Promise((resolve) => {
      document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
    });
  }
  return Promise.resolve();
}

export async function bootstrap(): Promise<WidgetInstance | null> {
  const script = findScriptTag();
  const publicKey = script?.dataset.webchatId?.trim();

  if (!publicKey) {
    console.error(
      `${LOG_PREFIX} não encontrei o atributo data-webchat-id. Exemplo: ` +
        '<script src=".../widget.js" data-webchat-id="pk_sua_chave" async></script>',
    );
    return null;
  }

  const resolvida = resolveApiBase(script);
  if (!resolvida) {
    console.error(`${LOG_PREFIX} não consegui descobrir a base da API. Informe data-api-base no script.`);
    return null;
  }
  const apiBase = resolvida.base;

  /*
   * **Avisa antes de tentar, quando a base foi adivinhada.**
   *
   * O palpite `<origem do script>/api` falha de um jeito que aponta para o lugar errado. Quando o widget
   * e a API estão em hosts diferentes — que é o nosso caso desde a migração para a VM —, o navegador
   * bloqueia a resposta por CORS **antes** de ela chegar ao nosso código, então nem o diagnóstico de
   * "veio HTML" do `fetchConfig` consegue rodar. O que o desenvolvedor vê é:
   *
   *     has been blocked by CORS policy: No 'Access-Control-Allow-Origin' header
   *
   * e vai investigar cabeçalhos, allowlist de domínio, configuração de CORS — nada disso. A causa é um
   * atributo ausente na tag, e o único jeito de dizê-la é falar **antes** da requisição, porque depois
   * dela o navegador não nos deixa mais falar.
   */
  if (resolvida.adivinhado) {
    console.warn(
      `${LOG_PREFIX} sem data-api-base no script; vou tentar ${apiBase}. ` +
        'Se a API não estiver nesse host, informe data-api-base — o painel gera o snippet completo. ' +
        'Erro de CORS logo abaixo costuma ser este atributo faltando, não configuração de CORS.',
    );
  }

  /*
   * `data-no-cache="true"` para ver a publicação sem esperar cache nenhum. A config vai com o
   * `max-age` de `CONFIG_CACHE_SECONDS` (ver `cache.ts` no engine) e passa pelo CDN do Hosting, então
   * mudança recém-publicada leva alguns segundos para aparecer numa página já carregada. Recarga
   * forçada limpa o navegador, não o CDN — só uma URL diferente resolve.
   *
   * Nasceu quando a espera era de até dois minutos e o desvio se pagava. Hoje raramente se justifica,
   * e não é o padrão: sem cache, cada visita vira uma invocação da Function.
   */
  const result = await fetchConfig(apiBase, publicKey, {
    noCache: script?.dataset.noCache === 'true',
  });

  if (!result.ok) {
    /*
     * A ordem aqui importa: os casos **específicos** vêm antes dos genéricos por código de status.
     * `webchat_suspended` também é 403, então posto depois do `if (status === 403)` ele seria
     * inalcançável — e o dono receberia a mensagem errada, sobre domínio autorizado.
     */
    if (result.status === 403 && result.error === 'webchat_suspended') {
      /*
       * Suspensão administrativa: o widget não é desenhado, e o aviso é para quem instalou a tag — não
       * é defeito nem limite de uso, é a conta. Separado dos tetos de propósito: ali quem resolve é o
       * dono, ajustando um número; aqui é o responsável pela conta.
       */
      console.warn(
        `${LOG_PREFIX} ${result.message}\n` +
          'A recusa fica em cache por até uma hora, então o widget pode levar esse tempo para voltar ' +
          'depois da reativação.',
      );
    } else if (result.status === 403) {
      console.error(
        `${LOG_PREFIX} o backend recusou a config para este domínio (HTTP 403: ${result.error}).\n` +
          `${result.message}\n` +
          `Domínio atual: ${window.location.hostname}. ` +
          'Adicione-o em "Domínios autorizados" no painel do webchat e publique novamente.',
      );
    } else if (result.status === 404 && result.error === 'not_published') {
      console.warn(`${LOG_PREFIX} este webchat ainda não foi publicado. Clique em "Publicar" no painel.`);
    } else if (
      result.status === 429 &&
      (result.error === 'quota_exceeded' || result.error === 'request_limit_exceeded')
    ) {
      /*
       * Teto mensal atingido: o widget não é desenhado. Aviso e não erro, porque não há defeito
       * nenhum — é um limite que o dono escolheu. A mensagem precisa dizer onde mexer, senão vira
       * ticket de "o chat sumiu do site".
       *
       * Os dois tetos são distinguidos porque a ação é diferente: `quota_exceeded` é volume de
       * conversas, `request_limit_exceeded` é volume de tráfego. Quem for depurar precisa saber qual
       * dos dois bateu — a mensagem do servidor já diz, e aqui o aviso acrescenta que a recusa fica
       * guardada no CDN, senão aumentar o teto e não ver efeito na hora parece um segundo defeito.
       */
      console.warn(
        `${LOG_PREFIX} ${result.message}\n` +
          'O widget não será exibido até o limite ser aumentado ou o mês virar. ' +
          'Depois de aumentar, a recusa ainda pode ficar em cache por até um minuto.',
      );
    } else {
      console.error(`${LOG_PREFIX} ${result.message}`);
    }
    return null;
  }

  const config = result.config;

  // Defesa em profundidade: mesmo com a config em mão, não desenha fora dos domínios declarados.
  if (!matchesDomain(window.location.hostname, config.authorizedDomains)) {
    console.error(
      `${LOG_PREFIX} o domínio "${window.location.hostname}" não está na lista de domínios ` +
        `autorizados deste webchat (${config.authorizedDomains.join(', ')}). O widget não será exibido.`,
    );
    return null;
  }

  await whenDomReady();

  const listeners = new Set<(event: WidgetEvent) => void>();
  const instance = mountWidget({
    config,
    apiBase,
    assetBase: resolveAssetBase(script),
    autoOpen: script?.dataset.autoOpen === 'true',
    onEvent: (event) => {
      for (const listener of listeners) {
        try {
          listener(event);
        } catch (error) {
          console.error(`${LOG_PREFIX} erro em um listener:`, error);
        }
      }
    },
  });

  const api: MerlinWebchatApi = {
    open: () => instance.open(),
    close: () => instance.close(),
    toggle: () => instance.toggle(),
    reset: () => instance.reset(),
    getVariables: () => instance.getVariables(),
    on(handler) {
      listeners.add(handler);
      return () => listeners.delete(handler);
    },
    ready: Promise.resolve(instance),
    version: SDK_VERSION,
  };

  (window as unknown as { MerlinWebchat: MerlinWebchatApi }).MerlinWebchat = api;
  return instance;
}
